const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

type Unit = {
	label: string;
	short: string;
	ms: number;
};

const UNITS: Unit[] = [
	{ label: "year", short: "y", ms: YEAR },
	{ label: "month", short: "mo", ms: MONTH },
	{ label: "week", short: "w", ms: WEEK },
	{ label: "day", short: "d", ms: DAY },
	{ label: "hour", short: "hr", ms: HOUR },
	{ label: "minute", short: "m", ms: MINUTE },
	{ label: "second", short: "s", ms: SECOND },
];

export type RelativeTimeInput = string | number | Date | undefined | null;

/**
 * Formats a timestamp relative to now (e.g. “3 days ago”, “in 2 hr”).
 *
 * Accepts ISO strings, epoch milliseconds, or a {@link Date}. Falsy or unparseable values
 * yield `""`. Sub-second difference yields `"just now"`.
 *
 * @param value - Instant to compare with `Date.now()`
 * @param format - `"long"` → words (“3 minutes ago”); `"short"` → compact units (“3m ago”)
 */
export const timeAgo = (value: RelativeTimeInput, format?: 'short' | 'long'): string => {
	const date = parseRelativeTime(value);

	

	if (!date) {
		return "";
	}

	const diffMs = Date.now() - date.getTime();

	if (Math.abs(diffMs) < SECOND) {
		return "just now";
	}

	const past = diffMs >= 0;
	const absDiff = Math.abs(diffMs);

	for (const unit of UNITS) {
		if (absDiff >= unit.ms || unit.label === "second") {
			const count = Math.floor(absDiff / unit.ms) || 1;
			const label = format === 'short' ? unit.short : unit.label;
			const noun = `${count}${format === "short" ? "" : " "}${label}${format === "short" ||count === 1 ? "" : "s"}`;
			return past ? `${noun} ago` : `in ${noun}`;
		}
	}

	return "";
};

/**
 * Buckets a timestamp into a calendar-day label ("Today", "Yesterday", or a
 * formatted date) for grouping list items by day.
 *
 * Unlike {@link timeAgo}, this is stable for an entire day rather than
 * changing every second - so day-grouped lists (e.g. `ArtifactPanel`) keep
 * new/existing items in the same group instead of every item landing in its
 * own transient "5s ago" bucket, which would defeat add/remove transitions
 * scoped to a group.
 */
export const dayBucketLabel = (value: RelativeTimeInput): string => {
	const date = parseRelativeTime(value);
	if (!date) {
		return "";
	}

	const startOfDay = (d: Date) =>
		new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / DAY);

	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Yesterday";

	const sameYear = date.getFullYear() === new Date().getFullYear();
	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: sameYear ? undefined : "numeric",
	});
};

const parseRelativeTime = (value: RelativeTimeInput): Date | null => {
	if (!value) {
		return null;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	if (typeof value === "number") {
		return new Date(value);
	}

	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Converts a number of seconds into a formatted time string.
 *
 * @example
 * secondsToTime(3665) // "1:01:05"
 * secondsToTime(125)  // "2:05"
 * secondsToTime(45)   // "45"
 */
export const secondsToTime = (seconds: number, format: "HH:MM:SS" | "MM:SS" | "SS" = "HH:MM:SS") => {
	seconds = Math.round(seconds);
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainingSeconds = seconds % 60;
	const remainingSecondsString = remainingSeconds.toString().padStart(2, "0");
	if (hours > 0) {
		if (format === "HH:MM:SS") {
			return `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSecondsString}`;
		} else if (format === "MM:SS") {
			return `${minutes}:${remainingSecondsString}`;
		} else {
			return `${remainingSecondsString}`;
		}
	} else if (minutes > 0) {
		if (format === "MM:SS" || format === "HH:MM:SS") {
			return `${minutes}:${remainingSecondsString}`;
		} else {
			return `${remainingSecondsString}`;
		}
	} else {
		if (format === "MM:SS") {
			return `0:${remainingSecondsString}`;
		} else {
			return `${remainingSecondsString}`;
		}
	}
};
